import Phaser from 'phaser';
import { GAME_CONFIG } from '../config/game';
import { setHighestLevel } from '../systems/SaveState';
import { getSoundSystem } from '../systems/SoundSystem';
import { calculateStars, saveLevelStars, calculateTimeStars, saveLevelTimeStars, getLevelTimeStars } from '../systems/StarRating';
import { getDailyBestScore } from '../systems/DailySave';
import { BackHandler, pushSceneState } from '../systems/BackButton';

export class LevelCompleteScene extends Phaser.Scene implements BackHandler {
  private level = 1;
  private score = 0;
  private bestScore = 0;
  private replay = false;
  private timeAttack = false;
  private daily = false;
  private dailyDate = '';
  private elapsedMs = 0;

  constructor() {
    super('LevelCompleteScene');
  }

  handleBack(): void {}

  init(data: { level: number; score: number; bestScore: number; replay?: boolean; timeAttack?: boolean; elapsedMs?: number; daily?: boolean; dailyDate?: string }): void {
    this.level = data.level;
    this.score = data.score;
    this.bestScore = data.bestScore;
    this.replay = data.replay ?? false;
    this.timeAttack = data.timeAttack ?? false;
    this.daily = data.daily ?? false;
    this.dailyDate = data.dailyDate ?? '';
    this.elapsedMs = data.elapsedMs ?? 0;
  }

  create(): void {
    pushSceneState();
    const centerX = GAME_CONFIG.width / 2;

    if (this.daily) {
      this.createDailyHeader(centerX);
      this.createDailyButtons(centerX);
    } else if (this.timeAttack) {
      this.createTimeAttackHeader(centerX);
      this.createButtons(centerX);
    } else {
      this.createNormalHeader(centerX);
      this.createButtons(centerX);
    }
  }

  private createNormalHeader(centerX: number): void {
    saveLevelStars(this.level, this.score);
    if (!this.replay) {
      setHighestLevel(this.level + 1);
    }
    const stars = calculateStars(this.score, this.level);
    const baseY = GAME_CONFIG.height / 2 - 50;

    this.add.text(centerX, baseY - 120, 'Land Restored!', {
      fontSize: '28px',
      color: '#a8e6cf',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5);

    const starDisplay = '★'.repeat(stars) + '☆'.repeat(3 - stars);
    this.add.text(centerX, baseY - 70, starDisplay, {
      fontSize: '32px',
      color: '#ffd700',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5);

    this.add.text(centerX, baseY - 30, `Level ${this.level} Complete`, {
      fontSize: '20px',
      color: '#ffffff',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5);

    this.add.text(centerX, baseY + 10, `Score: ${this.score}`, {
      fontSize: '18px',
      color: '#ffffff',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5);

    this.add.text(centerX, baseY + 40, `Best: ${this.bestScore}`, {
      fontSize: '16px',
      color: '#888888',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5);
  }

  private createTimeAttackHeader(centerX: number): void {
    saveLevelTimeStars(this.level, this.elapsedMs);
    const elapsedSeconds = this.elapsedMs / 1000;
    const stars = calculateTimeStars(elapsedSeconds, this.level);
    const baseY = GAME_CONFIG.height / 2 - 50;

    this.add.text(centerX, baseY - 120, 'Time Attack Complete!', {
      fontSize: '24px',
      color: '#7ec8e3',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5);

    const starDisplay = '★'.repeat(stars) + '☆'.repeat(3 - stars);
    this.add.text(centerX, baseY - 70, starDisplay, {
      fontSize: '32px',
      color: '#7ec8e3',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5);

    this.add.text(centerX, baseY - 30, `Level ${this.level}`, {
      fontSize: '20px',
      color: '#ffffff',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5);

    this.add.text(centerX, baseY + 10, this.formatTime(this.elapsedMs), {
      fontSize: '28px',
      color: '#ffffff',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5);

    const existing = getLevelTimeStars(this.level);
    if (existing) {
      this.add.text(centerX, baseY + 50, `Best: ${this.formatTime(existing.bestTime)}`, {
        fontSize: '16px',
        color: '#888888',
        fontFamily: 'sans-serif',
      }).setOrigin(0.5);
    }
  }

  private createDailyHeader(centerX: number): void {
    const baseY = GAME_CONFIG.height / 2 - 50;
    const bestScore = getDailyBestScore(this.dailyDate) ?? 0;

    this.add.text(centerX, baseY - 120, 'Challenge Complete!', {
      fontSize: '28px',
      color: '#f0d060',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5);

    this.add.text(centerX, baseY - 70, this.dailyDate, {
      fontSize: '18px',
      color: '#ffffff',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5);

    this.add.text(centerX, baseY + 10, `Score: ${this.score}`, {
      fontSize: '22px',
      color: '#ffffff',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5);

    if (bestScore > 0) {
      this.add.text(centerX, baseY + 50, `Best: ${bestScore}`, {
        fontSize: '16px',
        color: '#888888',
        fontFamily: 'sans-serif',
      }).setOrigin(0.5);
    }
  }

  private createDailyButtons(centerX: number): void {
    const baseY = GAME_CONFIG.height / 2 - 50;

    const retryBtn = this.add.text(centerX, baseY + 110, 'Play Again', {
      fontSize: '22px',
      color: '#1a1a2e',
      fontFamily: 'sans-serif',
      backgroundColor: '#f0d060',
      padding: { x: 32, y: 16 },
    }).setOrigin(0.5).setInteractive();

    retryBtn.on('pointerdown', () => {
      getSoundSystem().playBlip();
      this.scene.start('GameScene', { daily: true, date: this.dailyDate });
    });

    const calendarBtn = this.add.text(centerX, baseY + 180, 'Calendar', {
      fontSize: '18px',
      color: '#f0d060',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5).setInteractive();

    calendarBtn.on('pointerdown', () => {
      getSoundSystem().playBlip();
      this.scene.start('DailyCalendarScene');
    });

    const menuBtn = this.add.text(centerX, baseY + 220, 'Main Menu', {
      fontSize: '18px',
      color: '#888888',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5).setInteractive();

    menuBtn.on('pointerdown', () => {
      getSoundSystem().playBlip();
      this.scene.start('TitleScene');
    });
  }

  private createButtons(centerX: number): void {
    const accentColor = this.timeAttack ? '#7ec8e3' : '#a8e6cf';
    const baseY = GAME_CONFIG.height / 2 - 50;

    if (this.timeAttack || this.replay) {
      const retryBtn = this.add.text(centerX, baseY + 110, 'Play Again', {
        fontSize: '22px',
        color: '#1a1a2e',
        fontFamily: 'sans-serif',
        backgroundColor: accentColor,
        padding: { x: 32, y: 16 },
      }).setOrigin(0.5).setInteractive();

      retryBtn.on('pointerdown', () => {
        getSoundSystem().playBlip();
        this.scene.start('GameScene', { level: this.level, timeAttack: this.timeAttack });
      });
    } else {
      const continueBtn = this.add.text(centerX, baseY + 110, 'Continue', {
        fontSize: '22px',
        color: '#1a1a2e',
        fontFamily: 'sans-serif',
        backgroundColor: accentColor,
        padding: { x: 32, y: 16 },
      }).setOrigin(0.5).setInteractive();

      continueBtn.on('pointerdown', () => {
        getSoundSystem().playBlip();
        this.scene.start('GameScene', { level: this.level + 1 });
      });
    }

    const mapBtn = this.add.text(centerX, baseY + 180, 'World Map', {
      fontSize: '18px',
      color: accentColor,
      fontFamily: 'sans-serif',
    }).setOrigin(0.5).setInteractive();

    mapBtn.on('pointerdown', () => {
      getSoundSystem().playBlip();
      this.scene.start('WorldMapScene');
    });

    const menuBtn = this.add.text(centerX, baseY + 220, 'Main Menu', {
      fontSize: '18px',
      color: '#888888',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5).setInteractive();

    menuBtn.on('pointerdown', () => {
      getSoundSystem().playBlip();
      this.scene.start('TitleScene');
    });
  }

  private formatTime(ms: number): string {
    const totalSeconds = ms / 1000;
    if (totalSeconds >= 60) {
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = Math.floor(totalSeconds % 60);
      return `${minutes}:${String(seconds).padStart(2, '0')}`;
    }
    return `${totalSeconds.toFixed(1)}s`;
  }
}
